import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react"
import { useRouter } from "next/navigation"
import { LexicalText, Meeting } from "@/schema"
import { NoteTemplate } from "@/schema/note-template"
import { ReadonlyAttribute } from "@ui/forms/inline"
import { Button, ScrollArea, Skeleton } from "@ui/shad"
import { cn } from "@ui/utils"
import {
  Calendar,
  Check,
  ContactRound,
  ExternalLink,
  Link2,
  Link as LinkIcon,
  Loader2,
  Tag,
  Tags,
  Trash2,
  X,
} from "lucide-react"
import { toast } from "sonner"

import { isAllBooksId } from "@/lib/all-books"
import { useRenderNoteTemplate } from "@/hooks/api/company-note-templates"
import { useFetchFeatureFlags } from "@/hooks/api/feature-flags/queries"
import { useDeleteMeeting, useUpdateMeeting } from "@/hooks/api/meetings"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useFetchUsers } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { useDebounce } from "@/hooks/use-debounce"
import { Link } from "@/components/book-link"
import { ConfirmDialog } from "@/components/dialogs"
import { Editor, EditorRefHandle } from "@/components/lexical"
import { MeetingAuditLogLink } from "@/components/meetings/meeting-audit-log-link"
import { MeetingUnknownParticipants } from "@/components/meetings/meeting-participant-add-menu"
import MeetingTopicSelector from "@/components/meetings/meeting-topic-selector"
import MeetingTypeSelector from "@/components/meetings/meeting-type-selector"
import { MeetingNotepadHistoryButton } from "@/components/meetings/meeting-version-dialog"
import { UploadMeetingDropdown } from "@/components/meetings/upload-meeting-dropdown"
import { NoteTemplatePicker } from "@/components/notes/note-template-picker"

import AssociationsSelector from "./associations-selector"
import MeetingAttachments from "./meeting-attachments"
import { MeetingBookInfo } from "./meeting-book-info"
import MeetingDateTimeEditor from "./meeting-date-time-editor"

interface MeetingPastViewProps {
  meeting?: Meeting
  isLoading: boolean
  skinny?: boolean
  showBookInfo?: boolean
  onExternalLinkClick?: (e: MouseEvent) => void
}

export default function MeetingPastView({
  meeting,
  isLoading,
  skinny,
  showBookInfo,
  onExternalLinkClick,
}: MeetingPastViewProps) {
  const router = useRouter()
  const getContext = useApiContext()
  const { data: usersData } = useFetchUsers()
  const bookId = useBookId()
  const featureFlags = useFetchFeatureFlags()
  const meetingTopicsEnabled = featureFlags.data?.includes("meeting_topics") ?? false
  const shouldShowBookInfo = showBookInfo ?? isAllBooksId(bookId)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState("")
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const isSavingTitleRef = useRef(false)
  const updateMeeting = useUpdateMeeting()
  const deleteMeeting = useDeleteMeeting()
  const renderTemplate = useRenderNoteTemplate()
  const editorRef = useRef<EditorRefHandle>(null)
  const handleDeleteMeeting = async () => {
    if (!meeting) return
    await deleteMeeting.mutateAsync(meeting.id)
    toast.success("Meeting deleted")
    setShowDeleteConfirm(false)
    const context = await getContext()
    if (context?.bookId) {
      router.push(`/${context.bookId}/meetings`)
    }
  }

  const debouncedUpdateMeeting = useDebounce((lexicalText: LexicalText) => {
    if (!meeting?.id) return
    updateMeeting.mutate({ meetingId: meeting.id, data: { notepad: lexicalText } })
  }, 1000)

  const handleNotepadUpdate = useCallback(
    (lexicalText: LexicalText) => {
      debouncedUpdateMeeting(lexicalText)
    },
    [debouncedUpdateMeeting]
  )

  const handleApplyTemplate = useCallback(
    (template: NoteTemplate) => {
      if (!template.content || !meeting) return

      const applyContent = (content: LexicalText) => {
        editorRef.current?.updateContent(content)
        debouncedUpdateMeeting(content)
      }

      renderTemplate.mutate(
        { noteTemplateId: template.id, meetingId: meeting.id },
        {
          onSuccess: (data) => {
            if (data.note_template_render.content) {
              applyContent(data.note_template_render.content)
            } else {
              applyContent(template.content!)
            }
          },
          onError: () => {
            applyContent(template.content!)
          },
        }
      )
    },
    [meeting, renderTemplate, debouncedUpdateMeeting]
  )

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  const handleEditTitleClick = () => {
    setEditedTitle(meeting?.title || "Untitled meeting")
    setIsEditingTitle(true)
  }

  const handleSaveTitle = async () => {
    if (isSavingTitleRef.current) return

    if (!meeting || editedTitle.trim() === (meeting.title || "")) {
      setIsEditingTitle(false)
      return
    }

    isSavingTitleRef.current = true
    try {
      await updateMeeting.mutateAsync({
        meetingId: meeting.id,
        data: { title: editedTitle.trim() || undefined },
      })
      setIsEditingTitle(false)
    } catch {
      setEditedTitle(meeting.title || "Untitled meeting")
      setIsEditingTitle(false)
    } finally {
      isSavingTitleRef.current = false
    }
  }

  const handleCancelTitle = () => {
    setEditedTitle(meeting?.title || "Untitled meeting")
    setIsEditingTitle(false)
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSaveTitle()
    } else if (e.key === "Escape") {
      e.preventDefault()
      handleCancelTitle()
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-48px)] items-center justify-center">
        <Skeleton className="size-8 animate-spin" />
      </div>
    )
  }

  if (!meeting) {
    return (
      <div className="flex h-[calc(100vh-48px)] items-center justify-center">
        <span className="text-destructive">Failed to load meeting</span>
      </div>
    )
  }

  const title = meeting.title || "Untitled meeting"

  const unknownParticipants = meeting?.unknown_participants ?? []

  return (
    <ScrollArea className="h-[calc(100vh-48px)]">
      <div className="absolute top-2 right-2 z-10 flex gap-2">
        <Link href={`/meetings/${meeting.id}`} onClick={onExternalLinkClick}>
          <Button variant="ghost" size="iconXs">
            <ExternalLink />
          </Button>
        </Link>
        {meeting.can_delete && (
          <Button variant="ghost" size="iconXs" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 />
          </Button>
        )}
      </div>
      <div className={cn("mx-auto w-full px-4", skinny ? "max-w-[28vw] pt-6" : "max-w-screen-sm pt-20")}>
        {isEditingTitle ? (
          <div className="mt-2 flex items-center gap-2">
            <input
              ref={titleInputRef}
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              onBlur={handleSaveTitle}
              className={cn(
                "ring-border focus:ring-ring min-w-64 rounded border-0 bg-transparent px-1 py-0 ring-1 outline-none",
                skinny ? "text-xl font-semibold" : "text-2xl font-bold"
              )}
              style={{ width: `${Math.max(16, editedTitle.length * 0.8)}rem` }}
              disabled={updateMeeting.isPending}
            />
            <button
              onClick={handleSaveTitle}
              disabled={updateMeeting.isPending}
              className="hover:bg-muted rounded p-1 disabled:opacity-50"
              title="Save"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancelTitle}
              disabled={updateMeeting.isPending}
              className="hover:bg-muted rounded p-1 disabled:opacity-50"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div
            className={cn(
              "hover:text-muted-foreground -mx-1 mt-2 cursor-pointer rounded px-1 transition-colors",
              skinny ? "text-xl font-semibold" : "text-2xl font-bold"
            )}
            onClick={handleEditTitleClick}
            title="Click to edit title"
          >
            {title}
          </div>
        )}
        <MeetingAuditLogLink meeting={meeting} />
        {meeting.recording_capture_warning && (
          <p role="alert" className="text-red-accent mt-2 text-sm">
            {meeting.recording_capture_warning}
          </p>
        )}
        {meeting.live_recording_session ? (
          <LiveRecordingStatus status={meeting.live_recording_session.status} />
        ) : (
          <div className="mt-2">
            <UploadMeetingDropdown meetingId={meeting.id} bookId={meeting.book_id} />
          </div>
        )}

        <div className="mt-4 space-y-2 truncate">
          <ReadonlyAttribute
            label="Date & time"
            placeholder="No date configured"
            LabelIcon={Calendar}
            hideLabel
            renderValue={() => <MeetingDateTimeEditor meeting={meeting} />}
          />
          <ReadonlyAttribute
            label="Meeting type"
            LabelIcon={Tag}
            hideLabel
            renderValue={() => <MeetingTypeSelector meeting={meeting} size="sm" />}
          />
          {meetingTopicsEnabled && (
            <ReadonlyAttribute
              label="Meeting topics"
              LabelIcon={Tags}
              hideLabel
              renderValue={() => <MeetingTopicSelector meeting={meeting} />}
            />
          )}
          {shouldShowBookInfo && <MeetingBookInfo meeting={meeting} users={usersData?.users} />}
          <ReadonlyAttribute
            label="Conference link"
            value={meeting.meeting_link}
            placeholder="No link configured"
            LabelIcon={LinkIcon}
            href={meeting.meeting_link}
            hideLabel
          />
          <ReadonlyAttribute
            label="Associations"
            renderValue={() => <AssociationsSelector meeting={meeting} />}
            placeholder="No associations"
            LabelIcon={Link2}
            hideLabel
            className="items-start [&>*:first-child]:mt-1"
          />
          <ReadonlyAttribute
            label="Other attendees"
            renderValue={() => (
              <MeetingUnknownParticipants
                participants={unknownParticipants}
                emptyLabel="No other attendees"
                meetingId={meeting.id}
              />
            )}
            placeholder="No other attendees"
            LabelIcon={ContactRound}
            hideLabel
            className="items-start [&>*:first-child]:mt-1"
          />
          <MeetingAttachments meeting={meeting} />
        </div>

        <div className="mt-4 border-b pb-2" />

        <div className="relative pt-4">
          <div className="absolute top-4 right-0 z-10 flex gap-2">
            <MeetingNotepadHistoryButton meetingId={meeting.id} />
            <NoteTemplatePicker onApply={handleApplyTemplate} />
          </div>
          <Editor
            ref={editorRef}
            initialContent={meeting.notepad}
            editable={true}
            onChange={handleNotepadUpdate}
            placeholder="Start typing or press '/' for commands"
            startingPlaceholder="Add manual meeting notes here..."
          />
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={"Delete meeting?"}
        description={"This will permanently delete the meeting and all its data. This action cannot be undone."}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowDeleteConfirm(false) }}
        action={{ label: "Delete", onClick: handleDeleteMeeting }}
      />
    </ScrollArea>
  )
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Preparing...",
  uploading: "Uploading audio...",
  concatenating: "Processing audio...",
  error: "Processing failed",
}

function LiveRecordingStatus({ status }: { status: string }) {
  const label = STATUS_LABELS[status] || "Processing..."
  const isError = status === "error"

  return (
    <div
      className={cn(
        "mt-2 flex items-center gap-2 rounded-md px-3 py-2",
        isError ? "bg-red-background text-red-foreground" : "bg-muted"
      )}
    >
      {!isError && <Loader2 className="size-4 animate-spin" />}
      <span className="text-sm font-medium">{label}</span>
    </div>
  )
}
